import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { Tracker } from '@relewise/client';
import { RelewiseCart, RelewiseOrder } from '../types/tracking';
import { RelewiseUserService } from './UserService';

interface InitializedService {
    tracker: Tracker;
    relewiseUserService: RelewiseUserService;
    disabled: false;
}

export class RelewiseTrackingService {
    tracker: Tracker | undefined;
    protected relewiseUserService: RelewiseUserService | undefined;
    protected disabled = true;
    private isDev = process.env.NODE_ENV === 'development';

    constructor(request: IRequestContext) {
        const { relewiseApiKey, relewiseDatasetId, relewiseUrl } = request.app?.config ?? {};
        if (!relewiseApiKey || !relewiseDatasetId || !relewiseUrl) {
            console.warn(
                '[Relewise Tracking Service] Missing Relewise configuration. Expected relewiseApiKey, relewiseDatasetId and relewiseUrl. Add this config in the CMS.'
            );
            return;
        }

        this.tracker = new Tracker(relewiseDatasetId, relewiseApiKey, {
            serverUrl: relewiseUrl
        });

        this.relewiseUserService = new RelewiseUserService(request);

        this.disabled = false;
    }

    private logEvent(name: string, payload: Record<string, unknown>) {
        if (this.isDev) {
            console.log(`[Relewise Tracking Service] ${name}:`, payload);
        }
    }

    private isEnabled(): this is InitializedService {
        return !this.disabled && !!this.tracker && !!this.relewiseUserService;
    }

    public async trackProductView(productId: string, variantId?: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const payload = {
                productId,
                user,
                ...(variantId ? { variantId } : {})
            };

            this.logEvent('Product View', payload);
            await this.tracker.trackProductView(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackProductEngagement(productId: string, isFavorite: boolean, variantId?: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const payload = {
                user,
                product: {
                    productId,
                    ...(variantId ? { variantId } : {})
                },
                engagement: {
                    isFavorite
                }
            };

            this.logEvent('Product Engagement', payload);
            await this.tracker.trackProductEngagement(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackCategoryView(listId: string) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const payload = {
                idPath: [listId],
                user
            };

            this.logEvent('Product List View', payload);
            await this.tracker.trackProductCategoryView(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackOrder(order: RelewiseOrder) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const { lineItems, subtotal, orderNumber } = order;
            const payload = {
                lineItems,
                subtotal,
                orderNumber,
                user,
                cartName: 'default'
            };

            this.logEvent('Order', payload);
            await this.tracker.trackOrder(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackCart(cart: RelewiseCart) {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const { lineItems, subtotal } = cart;
            const payload = {
                lineItems,
                subtotal,
                user,
                cartName: 'default'
            };

            this.logEvent('Cart', payload);
            await this.tracker.trackCart(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }

    public async trackUserUpdate() {
        if (!this.isEnabled()) {
            return;
        }

        try {
            const user = await this.relewiseUserService.getUser();
            const payload = {
                user
            };

            this.logEvent('User update', payload);
            await this.tracker.trackUserUpdate(payload);
        } catch (err) {
            console.error('[Relewise Tracking Service] tracking failed', err);
        }
    }
}
